import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Divider, Form, Row, Switch, Tooltip } from 'antd'
import { IBaseFormProps, RentalInfoPeriod } from 'Modules'
import { useMemo } from 'react'

import { PTOTypeEnum, TOKEN_ADDRESS_MAP } from 'constants/pto'
import { useAppSetting } from 'hooks/setting/use-app-setting'

import RentalPeriodCard from './rental-period-card'

const RentalInformation = ({ form, edit = true, mode = 'create' }: IBaseFormProps) => {
  const ptoType = Form.useWatch('ptoType', form)
  const rentalPeriodsWatch = Form.useWatch('rentalPeriods', form)
  const { getSetting } = useAppSetting()

  const isAddDisabled = useMemo(() => {
    const now = Math.floor(Date.now() / 1000)

    const periods = (rentalPeriodsWatch || []) as RentalInfoPeriod[]
    if (!periods.length) return false

    // Last period incomplete (no endDate filled yet)
    const lastPeriod = periods[periods.length - 1]
    if (!lastPeriod?.endDate) return true

    const firstUnendedIndex = periods.findIndex((p) => p.endDate > now)

    if (firstUnendedIndex === -1) return false

    // Already has a future period after the ongoing one → only 1 ahead allowed
    return firstUnendedIndex < periods.length - 1
  }, [rentalPeriodsWatch])

  const { firstTokenSymbol, firstTokenIndex } = useMemo(() => {
    const periods = (rentalPeriodsWatch || []) as RentalInfoPeriod[]
    const index = periods.findIndex((p) => p?.tokenSymbol)
    return {
      firstTokenSymbol: index >= 0 ? periods[index].tokenSymbol : null,
      firstTokenIndex: index,
    }
  }, [rentalPeriodsWatch])

  if (ptoType !== PTOTypeEnum.RENTAL_PTO) return null

  const isEditMode = mode === 'edit'

  const isFieldLocked = (index: number) => {
    if (!isEditMode) return !edit
    const periods = rentalPeriodsWatch || []
    const period = periods[index]
    return period?.contractAddress ? true : !edit
  }

  return (
    <fieldset>
      <legend>Rental Information</legend>
      <Row justify="space-between" align="middle">
        <span>
          Leasing In Progress
          <Tooltip
            title={
              <div className="tooltip-box">
                <p className="tooltip-text">
                  Toggle the switch to enable show for user that the leasing is in progress in
                  Rental Detail page.
                </p>
              </div>
            }
            placement="right"
          >
            <QuestionCircleOutlined className="tooltip-icon" />
          </Tooltip>
        </span>
        <Form.Item name="isLeasingInProgress" valuePropName="checked" style={{ margin: 0 }}>
          <Switch disabled={!edit} />
        </Form.Item>
      </Row>

      <Divider />

      <Form.List name="rentalPeriods">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const periods = rentalPeriodsWatch || []
              const period = periods[name]
              const hasContract = isEditMode && !!period?.contractAddress

              return (
                <RentalPeriodCard
                  key={key}
                  name={name}
                  restField={restField}
                  form={form}
                  edit={edit}
                  isEditMode={isEditMode}
                  locked={isFieldLocked(name)}
                  hasContract={hasContract}
                  period={period}
                  firstTokenSymbol={name === firstTokenIndex ? null : firstTokenSymbol}
                  onRemove={() => remove(name)}
                />
              )
            })}

            {edit && (
              <Button
                type="dashed"
                disabled={isAddDisabled}
                onClick={() => {
                  const symbol = firstTokenSymbol || 'USDC'
                  const defaultAddress = getSetting(TOKEN_ADDRESS_MAP[symbol])
                  add({ tokenSymbol: symbol, tokenReward: defaultAddress || '' })
                }}
                block
                icon={<PlusOutlined />}
              >
                Add Rental Period
              </Button>
            )}
          </>
        )}
      </Form.List>
    </fieldset>
  )
}

export default RentalInformation
